import React, { useState, useEffect, useCallback } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { PeriodSelector } from '../components/ui/PeriodSelector';
import { getCashflow, getCashflowTransactions, CashflowEntry, CashflowTransaction } from '../services/apiService';
import { formatBRL } from '../utils/FormatDecimal';
import { getMonthRange } from '../hooks/dashboardUtils';
import { X } from 'lucide-react';

interface DrilldownParams {
  year: number;
  month: number;
  label: string;
  category: string;
  direction: string;
  categoryLabel: string;
  directionLabel: string;
}

export default function CashFlowPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<CashflowEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drilldown modal state
  const [drilldown, setDrilldown] = useState<DrilldownParams | null>(null);
  const [drilldownData, setDrilldownData] = useState<CashflowTransaction[] | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const { end } = getMonthRange(year, month);
    const beginYear = year - 1;
    const begin = `${beginYear}-${String(month).padStart(2, '0')}-01`;
    getCashflow({ begin, end })
      .then((result) => { if (!cancelled) setData(result.months); })
      .catch((err: any) => { if (!cancelled) setError(err.response?.data?.detail || 'Erro ao carregar fluxo de caixa'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, month]);

  const openDrilldown = useCallback(async (params: DrilldownParams) => {
    setDrilldown(params);
    setDrilldownData(null);
    setDrilldownLoading(true);
    try {
      const result = await getCashflowTransactions({
        year: params.year,
        month: params.month,
        category: params.category,
        direction: params.direction,
      });
      setDrilldownData(result.transactions);
    } catch {
      setDrilldownData([]);
    } finally {
      setDrilldownLoading(false);
    }
  }, []);

  if (loading && !data) return <LoadingSpinner message="Carregando fluxo de caixa..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data || data.length === 0) return <EmptyState title="Sem dados" description="Nenhum dado disponível" />;

  const hasFin = data.some(r => r.entradas_financiamento > 0 || r.saidas_financiamento > 0);
  const hasSoc = data.some(r => r.entradas_socios > 0 || r.saidas_socios > 0);
  const hasInv = data.some(r => r.entradas_investimento > 0 || r.saidas_investimento > 0);
  const hasProj = data.some(r => r.projected);

  // Split real vs projected into separate data keys for the chart
  const chartData = data.map(r => ({
    ...r,
    // Real data (null for projected months)
    r_entradas_op: r.projected ? null : r.entradas_operacional,
    r_saidas_op: r.projected ? null : r.saidas_operacional,
    r_entradas_fin: r.projected ? null : r.entradas_financiamento,
    r_saidas_fin: r.projected ? null : r.saidas_financiamento,
    r_entradas_soc: r.projected ? null : r.entradas_socios,
    r_saidas_soc: r.projected ? null : r.saidas_socios,
    r_entradas_inv: r.projected ? null : r.entradas_investimento,
    r_saidas_inv: r.projected ? null : r.saidas_investimento,
    // Projected data (null for real months)
    p_entradas_op: r.projected ? r.entradas_operacional : null,
    p_saidas_op: r.projected ? r.saidas_operacional : null,
    p_entradas_fin: r.projected ? r.entradas_financiamento : null,
    p_saidas_fin: r.projected ? r.saidas_financiamento : null,
    p_entradas_soc: r.projected ? r.entradas_socios : null,
    p_saidas_soc: r.projected ? r.saidas_socios : null,
    p_entradas_inv: r.projected ? r.entradas_investimento : null,
    p_saidas_inv: r.projected ? r.saidas_investimento : null,
    // Saldo line split
    saldo_real: r.projected ? null : r.saldo_acumulado,
    saldo_proj: r.projected ? r.saldo_acumulado : null,
  }));

  // Bridge: last real month also gets saldo_proj so the line connects
  if (hasProj) {
    const lastRealIdx = chartData.findIndex(r => r.projected) - 1;
    if (lastRealIdx >= 0) {
      chartData[lastRealIdx].saldo_proj = chartData[lastRealIdx].saldo_acumulado;
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodSelector year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        {loading && <span className="text-xs text-gray-400">Atualizando...</span>}
      </div>

      {/* Chart */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Fluxo de Caixa
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={chartData}>
              <defs>
                {/* Diagonal stripe patterns for projected bars */}
                <pattern id="stripe-green" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="3" height="6" fill="#22c55e" opacity="0.4" />
                </pattern>
                <pattern id="stripe-red" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="3" height="6" fill="#ef4444" opacity="0.4" />
                </pattern>
                <pattern id="stripe-indigo" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="3" height="6" fill="#6366f1" opacity="0.4" />
                </pattern>
                <pattern id="stripe-amber" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="3" height="6" fill="#f59e0b" opacity="0.4" />
                </pattern>
                <pattern id="stripe-sky" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="3" height="6" fill="#0ea5e9" opacity="0.4" />
                </pattern>
                <pattern id="stripe-teal" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="3" height="6" fill="#14b8a6" opacity="0.4" />
                </pattern>
                <pattern id="stripe-violet" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="3" height="6" fill="#8b5cf6" opacity="0.4" />
                </pattern>
                <pattern id="stripe-fuchsia" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="3" height="6" fill="#d946ef" opacity="0.4" />
                </pattern>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="bars" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: any) => formatBRL(Number(value))} />
              <Legend />
              {/* Real bars */}
              <Bar yAxisId="bars" dataKey="r_entradas_op" name="Entradas Operacional" fill="#22c55e" stackId="entradas" radius={[4, 4, 0, 0]} />
              {hasFin && <Bar yAxisId="bars" dataKey="r_entradas_fin" name="Entradas Financiamento" fill="#6366f1" stackId="entradas" />}
              {hasSoc && <Bar yAxisId="bars" dataKey="r_entradas_soc" name="Entradas Sócios" fill="#8b5cf6" stackId="entradas" />}
              {hasInv && <Bar yAxisId="bars" dataKey="r_entradas_inv" name="Entradas Investimento" fill="#0ea5e9" stackId="entradas" />}
              <Bar yAxisId="bars" dataKey="r_saidas_op" name="Saídas Operacional" fill="#ef4444" stackId="saidas" radius={[4, 4, 0, 0]} />
              {hasFin && <Bar yAxisId="bars" dataKey="r_saidas_fin" name="Saídas Financiamento" fill="#f59e0b" stackId="saidas" />}
              {hasSoc && <Bar yAxisId="bars" dataKey="r_saidas_soc" name="Saídas Sócios" fill="#d946ef" stackId="saidas" />}
              {hasInv && <Bar yAxisId="bars" dataKey="r_saidas_inv" name="Saídas Investimento" fill="#14b8a6" stackId="saidas" />}
              {/* Projected bars (striped) */}
              {hasProj && <Bar yAxisId="bars" dataKey="p_entradas_op" name="Entradas Op. (proj.)" fill="url(#stripe-green)" stackId="entradas_p" radius={[4, 4, 0, 0]} legendType="none" />}
              {hasProj && hasFin && <Bar yAxisId="bars" dataKey="p_entradas_fin" name="Entradas Fin. (proj.)" fill="url(#stripe-indigo)" stackId="entradas_p" legendType="none" />}
              {hasProj && hasSoc && <Bar yAxisId="bars" dataKey="p_entradas_soc" name="Entradas Sócios (proj.)" fill="url(#stripe-violet)" stackId="entradas_p" legendType="none" />}
              {hasProj && hasInv && <Bar yAxisId="bars" dataKey="p_entradas_inv" name="Entradas Inv. (proj.)" fill="url(#stripe-sky)" stackId="entradas_p" legendType="none" />}
              {hasProj && <Bar yAxisId="bars" dataKey="p_saidas_op" name="Saídas Op. (proj.)" fill="url(#stripe-red)" stackId="saidas_p" radius={[4, 4, 0, 0]} legendType="none" />}
              {hasProj && hasFin && <Bar yAxisId="bars" dataKey="p_saidas_fin" name="Saídas Fin. (proj.)" fill="url(#stripe-amber)" stackId="saidas_p" legendType="none" />}
              {hasProj && hasSoc && <Bar yAxisId="bars" dataKey="p_saidas_soc" name="Saídas Sócios (proj.)" fill="url(#stripe-fuchsia)" stackId="saidas_p" legendType="none" />}
              {hasProj && hasInv && <Bar yAxisId="bars" dataKey="p_saidas_inv" name="Saídas Inv. (proj.)" fill="url(#stripe-teal)" stackId="saidas_p" legendType="none" />}
              {/* Saldo line: solid for real, dashed for projected */}
              <Line yAxisId="line" type="monotone" dataKey="saldo_real" name="Saldo Acumulado" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              {hasProj && <Line yAxisId="line" type="monotone" dataKey="saldo_proj" name="Saldo Projetado" stroke="#3b82f6" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3, strokeDasharray: '' }} connectNulls={false} />}
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Detalhamento Mensal
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 px-3 font-medium text-gray-500" rowSpan={2}>Mês</th>
                  <th className="text-right py-3 px-3 font-medium text-blue-600" rowSpan={2}>Saldo Acum.</th>
                  <th className="text-center py-2 px-3 font-semibold text-gray-700 bg-slate-200/70 border-l-2 border-l-slate-400" colSpan={3}>Total</th>
                  <th className="text-center py-2 px-3 font-semibold text-emerald-800 bg-emerald-100/70 border-l-2 border-l-emerald-400" colSpan={3}>Operacional</th>
                  {hasFin && <th className="text-center py-2 px-3 font-semibold text-indigo-800 bg-indigo-100/70 border-l-2 border-l-indigo-400" colSpan={3}>Financiamento</th>}
                  {hasSoc && <th className="text-center py-2 px-3 font-semibold text-violet-800 bg-violet-100/70 border-l-2 border-l-violet-400" colSpan={3}>Sócios</th>}
                  {hasInv && <th className="text-center py-2 px-3 font-semibold text-sky-800 bg-sky-100/70 border-l-2 border-l-sky-400" colSpan={3}>Investimento</th>}
                </tr>
                <tr className="border-b border-gray-300">
                  <th className="text-right py-2 px-2 font-medium text-green-700 text-xs bg-slate-100 border-l-2 border-l-slate-400">Entr.</th>
                  <th className="text-right py-2 px-2 font-medium text-red-700 text-xs bg-slate-100">Saíd.</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-700 text-xs bg-slate-100 border-l border-slate-300">Res.</th>
                  <th className="text-right py-2 px-2 font-medium text-green-600 text-xs bg-emerald-50 border-l-2 border-l-emerald-400">Entr.</th>
                  <th className="text-right py-2 px-2 font-medium text-red-600 text-xs bg-emerald-50">Saíd.</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-700 text-xs bg-emerald-50 border-l border-emerald-200">Res.</th>
                  {hasFin && <th className="text-right py-2 px-2 font-medium text-indigo-600 text-xs bg-indigo-50 border-l-2 border-l-indigo-400">Entr.</th>}
                  {hasFin && <th className="text-right py-2 px-2 font-medium text-amber-600 text-xs bg-indigo-50">Saíd.</th>}
                  {hasFin && <th className="text-right py-2 px-2 font-medium text-gray-700 text-xs bg-indigo-50 border-l border-indigo-200">Res.</th>}
                  {hasSoc && <th className="text-right py-2 px-2 font-medium text-violet-600 text-xs bg-violet-50 border-l-2 border-l-violet-400">Entr.</th>}
                  {hasSoc && <th className="text-right py-2 px-2 font-medium text-fuchsia-600 text-xs bg-violet-50">Saíd.</th>}
                  {hasSoc && <th className="text-right py-2 px-2 font-medium text-gray-700 text-xs bg-violet-50 border-l border-violet-200">Res.</th>}
                  {hasInv && <th className="text-right py-2 px-2 font-medium text-sky-600 text-xs bg-sky-50 border-l-2 border-l-sky-400">Entr.</th>}
                  {hasInv && <th className="text-right py-2 px-2 font-medium text-teal-600 text-xs bg-sky-50">Saíd.</th>}
                  {hasInv && <th className="text-right py-2 px-2 font-medium text-gray-700 text-xs bg-sky-50 border-l border-sky-200">Res.</th>}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const stripe = i % 2 === 1;
                  const click = (category: string, direction: string, categoryLabel: string, directionLabel: string) => () => {
                    openDrilldown({
                      year: row.year, month: row.month, label: row.label,
                      category, direction, categoryLabel, directionLabel,
                    });
                  };
                  const clickable = "cursor-pointer hover:underline decoration-dotted";
                  const resultOp = row.entradas_operacional - row.saidas_operacional;
                  const resultFin = row.entradas_financiamento - row.saidas_financiamento;
                  const resultSoc = row.entradas_socios - row.saidas_socios;
                  const resultInv = row.entradas_investimento - row.saidas_investimento;
                  const resultTotal = row.entradas - row.saidas;
                  const resultColor = (v: number) => v >= 0 ? 'text-green-700' : 'text-red-700';
                  const proj = row.projected;
                  return (
                    <tr key={`${row.year}-${row.month}`} className={`border-b border-gray-100 hover:bg-blue-50/50 ${proj ? 'opacity-60' : ''}`}>
                      <td className={`py-3 px-3 font-medium ${stripe ? 'bg-gray-50' : ''} ${proj ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                        {row.label}{proj ? ' *' : ''}
                      </td>
                      <td className={`py-3 px-3 text-right font-mono font-medium ${row.saldo_acumulado >= 0 ? 'text-blue-600' : 'text-orange-600'} ${stripe ? 'bg-gray-50' : ''}`}>
                        {formatBRL(row.saldo_acumulado)}
                      </td>
                      {/* Total group */}
                      <td className={`py-3 px-2 text-right font-mono font-medium border-l-2 border-l-slate-400 ${stripe ? 'bg-slate-100/80' : 'bg-slate-50/50'}`}>
                        <span className={`text-green-700 ${clickable}`} onClick={click('total', 'entradas', 'Total', 'Entradas')}>
                          {formatBRL(row.entradas)}
                        </span>
                      </td>
                      <td className={`py-3 px-2 text-right font-mono font-medium ${stripe ? 'bg-slate-100/80' : 'bg-slate-50/50'}`}>
                        <span className={`text-red-700 ${clickable}`} onClick={click('total', 'saidas', 'Total', 'Saídas')}>
                          {formatBRL(row.saidas)}
                        </span>
                      </td>
                      <td className={`py-3 px-2 text-right font-mono font-bold border-l border-slate-300 ${resultColor(resultTotal)} ${stripe ? 'bg-slate-100/80' : 'bg-slate-50/50'}`}>
                        {formatBRL(resultTotal)}
                      </td>
                      {/* Operacional group */}
                      <td className={`py-3 px-2 text-right font-mono border-l-2 border-l-emerald-400 ${stripe ? 'bg-emerald-50/60' : 'bg-emerald-50/30'}`}>
                        <span className={`text-green-600 ${clickable}`} onClick={click('operacional', 'entradas', 'Operacional', 'Entradas')}>
                          {formatBRL(row.entradas_operacional)}
                        </span>
                      </td>
                      <td className={`py-3 px-2 text-right font-mono ${stripe ? 'bg-emerald-50/60' : 'bg-emerald-50/30'}`}>
                        <span className={`text-red-600 ${clickable}`} onClick={click('operacional', 'saidas', 'Operacional', 'Saídas')}>
                          {formatBRL(row.saidas_operacional)}
                        </span>
                      </td>
                      <td className={`py-3 px-2 text-right font-mono font-medium border-l border-emerald-200 ${resultColor(resultOp)} ${stripe ? 'bg-emerald-50/60' : 'bg-emerald-50/30'}`}>
                        {formatBRL(resultOp)}
                      </td>
                      {/* Financiamento group */}
                      {hasFin && (
                        <td className={`py-3 px-2 text-right font-mono border-l-2 border-l-indigo-400 ${stripe ? 'bg-indigo-50/60' : 'bg-indigo-50/30'}`}>
                          <span className={`text-indigo-600 ${clickable}`} onClick={click('financiamento', 'entradas', 'Financiamento', 'Entradas')}>
                            {formatBRL(row.entradas_financiamento)}
                          </span>
                        </td>
                      )}
                      {hasFin && (
                        <td className={`py-3 px-2 text-right font-mono ${stripe ? 'bg-indigo-50/60' : 'bg-indigo-50/30'}`}>
                          <span className={`text-amber-600 ${clickable}`} onClick={click('financiamento', 'saidas', 'Financiamento', 'Saídas')}>
                            {formatBRL(row.saidas_financiamento)}
                          </span>
                        </td>
                      )}
                      {hasFin && (
                        <td className={`py-3 px-2 text-right font-mono font-medium border-l border-indigo-200 ${resultColor(resultFin)} ${stripe ? 'bg-indigo-50/60' : 'bg-indigo-50/30'}`}>
                          {formatBRL(resultFin)}
                        </td>
                      )}
                      {/* Sócios group */}
                      {hasSoc && (
                        <td className={`py-3 px-2 text-right font-mono border-l-2 border-l-violet-400 ${stripe ? 'bg-violet-50/60' : 'bg-violet-50/30'}`}>
                          <span className={`text-violet-600 ${clickable}`} onClick={click('socios', 'entradas', 'Sócios', 'Entradas')}>
                            {formatBRL(row.entradas_socios)}
                          </span>
                        </td>
                      )}
                      {hasSoc && (
                        <td className={`py-3 px-2 text-right font-mono ${stripe ? 'bg-violet-50/60' : 'bg-violet-50/30'}`}>
                          <span className={`text-fuchsia-600 ${clickable}`} onClick={click('socios', 'saidas', 'Sócios', 'Saídas')}>
                            {formatBRL(row.saidas_socios)}
                          </span>
                        </td>
                      )}
                      {hasSoc && (
                        <td className={`py-3 px-2 text-right font-mono font-medium border-l border-violet-200 ${resultColor(resultSoc)} ${stripe ? 'bg-violet-50/60' : 'bg-violet-50/30'}`}>
                          {formatBRL(resultSoc)}
                        </td>
                      )}
                      {/* Investimento group */}
                      {hasInv && (
                        <td className={`py-3 px-2 text-right font-mono border-l-2 border-l-sky-400 ${stripe ? 'bg-sky-50/60' : 'bg-sky-50/30'}`}>
                          <span className={`text-sky-600 ${clickable}`} onClick={click('investimento', 'entradas', 'Investimento', 'Entradas')}>
                            {formatBRL(row.entradas_investimento)}
                          </span>
                        </td>
                      )}
                      {hasInv && (
                        <td className={`py-3 px-2 text-right font-mono ${stripe ? 'bg-sky-50/60' : 'bg-sky-50/30'}`}>
                          <span className={`text-teal-600 ${clickable}`} onClick={click('investimento', 'saidas', 'Investimento', 'Saídas')}>
                            {formatBRL(row.saidas_investimento)}
                          </span>
                        </td>
                      )}
                      {hasInv && (
                        <td className={`py-3 px-2 text-right font-mono font-medium border-l border-sky-200 ${resultColor(resultInv)} ${stripe ? 'bg-sky-50/60' : 'bg-sky-50/30'}`}>
                          {formatBRL(resultInv)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.some(r => r.projected) && (
              <p className="text-xs text-gray-400 mt-3 italic">* Meses projetados com base na mediana dos últimos 3 meses reais</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Drilldown Modal */}
      {drilldown && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDrilldown(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {drilldown.directionLabel} — {drilldown.categoryLabel}
                </h3>
                <p className="text-sm text-gray-500">{drilldown.label}</p>
              </div>
              <button onClick={() => setDrilldown(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {drilldownLoading ? (
                <div className="text-center py-8 text-gray-400">Carregando...</div>
              ) : !drilldownData || drilldownData.length === 0 ? (
                <div className="text-center py-8 text-gray-400">Nenhuma transação</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Data</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Descrição</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Contra-contas</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Valor</th>
                      <th className="text-right py-2 px-3 font-medium text-blue-600">Saldo Parcial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownData.map((txn, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-blue-50 even:bg-gray-50">
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{txn.date}</td>
                        <td className="py-2 px-3 text-gray-800">{txn.payee}</td>
                        <td className="py-2 px-3 text-gray-500 text-xs">{txn.contras.join(', ')}</td>
                        <td className="py-2 px-3 text-right font-mono text-gray-800">{formatBRL(txn.amount)}</td>
                        <td className="py-2 px-3 text-right font-mono text-blue-600 font-medium">{formatBRL(txn.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
